import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExpenseForm from './ExpenseForm';
import './ExpenseDashboard.css';
import { API_BASE_URL } from '../lib/api';

function ExpenseDashboard() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filter, setFilter] = useState('all'); // all, subscription, bill, insurance, etc.

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/expenses`, {
        withCredentials: true
      });
      setExpenses(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setError('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/expenses/summary`, {
        withCredentials: true
      });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/expenses/${expenseId}`, {
        withCredentials: true
      });
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      console.error('Error deleting expense:', err);
      alert('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingExpense(null);
    fetchExpenses();
    fetchSummary();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingExpense(null);
  };

  const filteredExpenses = filter === 'all'
    ? expenses
    : expenses.filter(e => e.category.toLowerCase() === filter.toLowerCase());

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getFrequencyBadge = (frequency) => {
    const badges = {
      monthly: { label: 'Monthly', class: 'badge-monthly' },
      yearly: { label: 'Yearly', class: 'badge-yearly' },
      quarterly: { label: 'Quarterly', class: 'badge-quarterly' },
      'one-time': { label: 'One-time', class: 'badge-onetime' }
    };
    const badge = badges[frequency] || { label: frequency, class: '' };
    return <span className={`frequency-badge ${badge.class}`}>{badge.label}</span>;
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="expense-dashboard">
        <div className="loading">Loading expenses...</div>
      </div>
    );
  }

  return (
    <div className="expense-dashboard">
      <div className="expense-header">
        <h2>💸 Expense Tracker</h2>
        <button className="btn-add" onClick={() => setShowForm(true)}>
          + Add Expense
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Summary Cards */}
      {summary && (
        <div className="expense-summary">
          <div className="summary-card">
            <div className="summary-label">Monthly Total</div>
            <div className="summary-value">{formatCurrency(summary.monthlyTotal)}</div>
            <div className="summary-subtitle">{summary.totalCount} expenses</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Yearly Total</div>
            <div className="summary-value">{formatCurrency(summary.yearlyTotal)}</div>
            <div className="summary-subtitle">Projected annual</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Upcoming Renewals</div>
            <div className="summary-value">{summary.upcomingRenewals?.length || 0}</div>
            <div className="summary-subtitle">Auto-renewing</div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && summary.breakdown && Object.keys(summary.breakdown).length > 0 && (
        <div className="category-breakdown">
          <h3>📊 Breakdown by Category</h3>
          <div className="breakdown-grid">
            {Object.entries(summary.breakdown)
              .sort((a, b) => b[1].monthlyTotal - a[1].monthlyTotal)
              .map(([category, data]) => (
                <div key={category} className="breakdown-card">
                  <div className="breakdown-category">{category}</div>
                  <div className="breakdown-count">{data.count} {data.count === 1 ? 'expense' : 'expenses'}</div>
                  <div className="breakdown-monthly">{formatCurrency(data.monthlyTotal)}/mo</div>
                  <div className="breakdown-yearly">{formatCurrency(data.yearlyTotal)}/yr</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="expense-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({expenses.length})
        </button>
        {['Subscription', 'Insurance', 'Bill', 'Utilities', 'Other'].map(cat => {
          const count = expenses.filter(e => e.category === cat).length;
          return count > 0 ? (
            <button
              key={cat}
              className={filter === cat ? 'active' : ''}
              onClick={() => setFilter(cat)}
            >
              {cat} ({count})
            </button>
          ) : null;
        })}
      </div>

      {/* Expense List */}
      {filteredExpenses.length === 0 ? (
        <div className="empty-state">
          <p>No expenses found. Add your first expense to get started!</p>
        </div>
      ) : (
        <div className="expense-table-container">
          <table className="expense-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Frequency</th>
                <th>Monthly Cost</th>
                <th>Start Date</th>
                <th>Auto-Renew</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map(expense => {
                // Calculate monthly cost
                let monthlyCost = expense.amount;
                if (expense.frequency === 'yearly') monthlyCost = expense.amount / 12;
                if (expense.frequency === 'quarterly') monthlyCost = expense.amount / 3;
                if (expense.frequency === 'one-time') monthlyCost = 0;

                return (
                  <tr key={expense.expenseId}>
                    <td className="expense-name">
                      {expense.name}
                      {expense.notes && (
                        <span className="expense-notes" title={expense.notes}>
                          📝
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="category-badge">{expense.category}</span>
                    </td>
                    <td className="amount">
                      {formatCurrency(expense.amount, expense.currency)}
                    </td>
                    <td>{getFrequencyBadge(expense.frequency)}</td>
                    <td className="monthly-cost">
                      {monthlyCost > 0 ? formatCurrency(monthlyCost, expense.currency) : '-'}
                    </td>
                    <td>{new Date(expense.startDate).toLocaleDateString()}</td>
                    <td className="auto-renew">
                      {expense.frequency !== 'one-time' && (
                        expense.autoRenew ? '✅ Yes' : '❌ No'
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(expense)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(expense.expenseId)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ExpenseForm
          expense={editingExpense}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
}

export default ExpenseDashboard;
